import { database } from './core';
import { ref, get, onValue } from 'firebase/database';
import { setData, removeData } from './api-wrapper.service';
// Notification creation is handled server-side by like listener Cloud Functions
import logger from '../../utils/logger';

/**
 * Service for handling likes functionality across different content types
 */

// Define types for likes related operations
export type ContentType = string;

export interface LikeData {
  date: number;
  senderUid: string;
  senderUsername: string;
  displayName?: string;
  photoURL?: string | null;
}

export interface LikeUserProfile {
  username?: string;
  displayName?: string;
  photoURL?: string | null;
}

/**
 * Toggle like status for a content item
 * @param contentId ID of the content to like/unlike
 * @param userId User ID liking/unliking the item
 * @param like True to like, false to unlike
 * @param userProfile User profile data to include with the like
 * @param contentOwner Optional owner ID - if provided, skips content lookup
 * @param contentType Optional content type - if provided, skips content lookup
 */
export const toggleLike = async (
  contentId: string,
  userId: string,
  like: boolean,
  userProfile?: LikeUserProfile,
  contentOwner?: string,
  contentType?: string
): Promise<void> => {
  try {
    logger.info(`🔵 LIKE SERVICE START - Toggling like for content ID: ${contentId}, like: ${like}, user: ${userId}`);
    logger.info(`🔵 Params: owner=${contentOwner}, type=${contentType}`);
    
    // Get the content item to determine its type and owner
    let contentData: any = null;
    
    // If owner and type are provided, skip the lookup entirely
    if (contentOwner && contentType) {
      logger.info(`🟢 Using provided owner: ${contentOwner} and type: ${contentType} for content: ${contentId}`);
      contentData = { owner: contentOwner, postType: contentType };
    } else if (contentOwner) {
      logger.info(`🟡 Using provided owner: ${contentOwner} for content: ${contentId} (no type provided)`);
      contentData = { owner: contentOwner };
    } else {
      logger.info(`🔴 No owner provided, looking up content in database`);
      const feedRef = ref(database, `publicFeed/${contentId}`);
      const feedSnapshot = await get(feedRef);
      contentData = feedSnapshot.exists() ? feedSnapshot.val() : null;
      logger.info("Content data from public feed:", contentData);
    }
    
    // If not found in public feed and no owner provided, try various content types directly
    if (!contentData || !contentData.owner) {
      logger.info(`Content not found in public feed, trying direct paths for ID: ${contentId}`);
      
      // Try trainings first - this is the most common
      const trainingRef = ref(database, `trainings/${contentId}`);
      const trainingSnapshot = await get(trainingRef);
      if (trainingSnapshot.exists()) {
        logger.info(`Found content as training: ${contentId}`);
        contentData = {
          ...trainingSnapshot.val(),
          postType: 'training',
          owner: trainingSnapshot.val().owner || userId // Fallback to current user if no owner
        };
      }
      
      // Competitions are directly accessible
      if (!contentData || !contentData.owner) {
        const compRef = ref(database, `competitions/${contentId}`);
        const compSnapshot = await get(compRef);
        if (compSnapshot.exists()) {
          logger.info(`Found content as competition: ${contentId}`);
          contentData = {
            ...compSnapshot.val(),
            postType: 'competition',
            owner: compSnapshot.val().owner || userId // Fallback to current user if no owner
          };
        }
      }
      
      // For other types, need to search more broadly - check user's own posts first
      if (!contentData || !contentData.owner) {
        // Look in the current user's posts - this works for posts they created
        const userPostRef = ref(database, `posts/${userId}/${contentId}`);
        const userPostSnapshot = await get(userPostRef);
        if (userPostSnapshot.exists()) {
          logger.info(`Found content in user's own posts: ${contentId}`);
          contentData = {
            ...userPostSnapshot.val(),
            postType: 'post',
            owner: userId
          };
        }
      }
      
      // For training items, we can also look in user's feed
      if (!contentData || !contentData.owner) {
        const userFeedRef = ref(database, `feeds/${userId}/${contentId}`);
        const userFeedSnapshot = await get(userFeedRef);
        if (userFeedSnapshot.exists()) {
          logger.info(`Found content in user's feed: ${contentId}`);
          contentData = userFeedSnapshot.val();
        }
      }
      
      // If we STILL don't have content data, assume it's the current user's content
      if (!contentData || !contentData.owner) {
        logger.info(`Creating minimal content data with current user as owner: ${contentId}`);
        // This is a fallback - in a real app, you might want to cancel the operation
        // But for testing purposes, we'll use the current user as owner
        contentData = {
          postType: contentType || 'training', // Use provided contentType or assume training
          owner: userId,
          id: contentId
        };
      }
    }
    
    // Default post type if not found - use provided contentType if available
    const postType = contentData?.postType || contentData?.type || contentType || 'training';
    const postOwner = contentData?.owner || contentOwner || userId; // Use provided owner first, then fallback
    
    logger.info(`📍 FINAL VALUES - Post type: ${postType}, owner: ${postOwner} for like action`);
    
    // We now always have a postOwner since we fallback to userId
    // But let's log if we're relying on the fallback
    if (!contentData?.owner && postOwner === userId) {
      logger.warn(`Using current user (${userId}) as fallback owner for content: ${contentId}`);
    }
    
    // Prepare like data object
    const likeData: LikeData = {
      date: Date.now(),
      senderUid: userId,
      senderUsername: userProfile?.username || 'User',
      displayName: userProfile?.displayName,
      photoURL: userProfile?.photoURL || null
    };
    
    // Construct Firebase paths based on post type
    const likePaths: string[] = [];
    
    // Determine primary content path
    logger.info(`🔧 Building like paths for postType: ${postType}`);
    
    switch (postType) {
      case 'training':
        const trainingPath = `trainings/${contentId}/likes/${userId}`;
        logger.info(`📝 Training path: ${trainingPath}`);
        likePaths.push(trainingPath);
        break;
      
      case 'competition':
        likePaths.push(`competitions/${contentId}/likes/${userId}`);
        break;
      
      case 'beltPromotion':
        if (postOwner) {
          likePaths.push(`beltPromotions/${postOwner}/${contentId}/likes/${userId}`);
        }
        break;
        
      case 'exercise':
        if (postOwner) {
          likePaths.push(`exercises/${postOwner}/${contentId}/likes/${userId}`);
        }
        break;
        
      case 'story':
        if (postOwner) {
          likePaths.push(`stories/${postOwner}/${contentId}/likes/${userId}`);
        }
        break;
        
      case 'workoutSession':
        if (postOwner) {
          likePaths.push(`workoutSessions/${postOwner}/${contentId}/likes/${userId}`);
        }
        break;
      
      case 'topic':
        if (postOwner) {
          const topicPath = `topics/${postOwner}/${contentId}/likes/${userId}`;
          logger.info(`📝 Topic path: ${topicPath}`);
          likePaths.push(topicPath);
        } else {
          logger.error(`❌ Cannot create topic like path - no owner!`);
        }
        break;
        
      default:
        // Default post path
        if (postOwner) {
          likePaths.push(`posts/${postOwner}/${contentId}/likes/${userId}`);
        }
    }
    
    // Don't update feed paths directly - they are managed by Cloud Functions
    // The feeds are read-only according to Firebase rules
    
    logger.info(`✅ Will update ${likePaths.length} paths for content ID ${contentId} (${postType}) to ${like}`);
    
    // IMPORTANT: Original Marune app does NOT update count fields directly!
    // The count is calculated by Cloud Functions when they detect changes to likes
    // We should ONLY write/remove the like data itself
    
    // Use a batch-like approach - we'll wait for all operations to complete
    const operations: Promise<void>[] = [];
    
    if (like) {
      // Set like data in all paths (via api-wrapper so writes get
      // ensureDatabaseOnline + retry handling)
      for (const path of likePaths) {
        logger.info(`➕ Writing like to: ${path}`);
        operations.push(setData(path, likeData));
      }
    } else {
      // Remove like data from all paths (via api-wrapper so writes get
      // ensureDatabaseOnline + retry handling)
      for (const path of likePaths) {
        logger.info(`➖ Removing like from: ${path}`);
        operations.push(removeData(path));
      }
    }
    
    // Wait for all updates to complete
    await Promise.all(operations);
    logger.info(`Successfully ${like ? 'liked' : 'unliked'} content ${contentId}`);
    
    // Like notifications are handled server-side by Cloud Functions
    // (sendNewOSSNotificationForTrainings, etc.)
  } catch (error) {
    logger.error('Error toggling like status:', error);
    throw error;
  }
};

/**
 * Get likes for a specific content item
 * @param contentId ID of the content to get likes for
 * @param contentType Type of the content
 */
export const getLikes = async (
  contentId: string,
  contentType: ContentType = 'post'
): Promise<Record<string, LikeData>> => {
  try {
    // Try to find the content to determine its location
    // First check public feed
    const publicRef = ref(database, `publicFeed/${contentId}/likes`);
    const publicSnapshot = await get(publicRef);
    
    if (publicSnapshot.exists()) {
      return publicSnapshot.val() || {};
    }
    
    // Try content-specific paths based on type
    let contentRef;
    switch (contentType) {
      case 'training':
        contentRef = ref(database, `trainings/${contentId}/likes`);
        break;
      case 'competition':
        contentRef = ref(database, `competitions/${contentId}/likes`);
        break;
      default:
        // For other types, we need the owner ID
        // This is a limitation since we're not storing this information
        // For now we'll return an empty object
        logger.info(`Cannot get likes for ${contentType} without owner ID`);
        return {};
    }
    
    // If we have a path, fetch the likes
    if (contentRef) {
      const snapshot = await get(contentRef);
      return snapshot.exists() ? snapshot.val() : {};
    }
    
    return {};
  } catch (error) {
    logger.error('Error getting likes:', error);
    return {};
  }
};

/**
 * Subscribe to likes for a specific content item
 * @param contentId ID of the content to subscribe to likes for
 * @param callback Function to call when likes change
 * @param contentType Type of the content
 */
export const subscribeLikes = (
  contentId: string,
  callback: (likes: Record<string, LikeData>) => void,
  contentType: ContentType = 'post'
): (() => void) => {
  // Try to find the content path with the most reliable data
  const publicRef = ref(database, `publicFeed/${contentId}/likes`);

  // Set up the listener - onValue returns an unsubscribe scoped to THIS
  // listener. A bare off(publicRef) would detach EVERY listener at the path,
  // so unmounting one subscriber killed all others on the same content.
  const unsubscribe = onValue(publicRef, (snapshot) => {
    if (snapshot.exists()) {
      callback(snapshot.val() || {});
    } else {
      callback({});
    }
  });

  // Return unsubscribe function
  return unsubscribe;
};

const exportedObject = {
  toggleLike,
  getLikes,
  subscribeLikes
};

export default exportedObject;